// Customer's cashback balance + redemption + referral + Telegram link panel.
// Opened from the sidebar's "$" button. All data fetched on mount.
//
// Editorial pass (redesign): hero balance is the centrepiece — set as
// large Plex Serif persimmon over the cream paper, framed as a printed
// passbook / banknote. Section labels Mono uppercase, list rows on
// paperSoft cards. Cashback earnings render in bottle green, redemptions
// in persimmon, debits in danger red.
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { Btn, Inp, Modal, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

// Tiny helper: section heading shared across all blocks here.
function SectionLabel({ children, style }) {
  return (
    <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 10, ...style }}>
      {children}
    </Mono>
  );
}

export function BalanceModal({ user, onClose }) {
  const toast = useToast();
  const t = useT();
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [reds, setReds] = useState([]);
  const [err, setErr] = useState("");
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState("");
  const [redeemMethod, setRedeemMethod] = useState("order_discount");
  const [redeemEmail, setRedeemEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tgLink, setTgLink] = useState(null);
  const [tgCode, setTgCode] = useState(null);
  const [tgErr, setTgErr] = useState("");

  const reload = async () => {
    try {
      const [b, t, r, tl] = await Promise.all([
        api.getCashbackBalance(),
        api.getCashbackTransactions({ limit: 20 }),
        api.getMyRedemptions(),
        api.getTelegramLink().catch(() => ({ link: null })),
      ]);
      setData(b);
      setTxs(t);
      setReds(r);
      setTgLink(tl.link);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { reload(); }, []);

  const fmt = (cents) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

  const refLink = user?.referralCode
    ? `${window.location.origin}/?ref=${user.referralCode}`
    : "";
  const copyRefLink = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const submitRedemption = async () => {
    setErr("");
    const dollars = parseFloat(redeemAmount);
    if (isNaN(dollars) || dollars < 5) { setErr(t("balance.minRedeem")); return; }
    setSubmitting(true);
    try {
      const details = (redeemMethod === "amazon_gift_card" || redeemMethod === "walmart_gift_card")
        ? { email: redeemEmail }
        : {};
      await api.createRedemption(Math.round(dollars * 100), redeemMethod, details);
      setShowRedeem(false);
      setRedeemAmount("");
      await reload();
    } catch (e) { setErr(e.message); }
    setSubmitting(false);
  };

  // Pending redemptions split out so we can render them in the dedicated
  // "in progress" block without re-filtering on every line.
  const pendingReds = reds.filter(r => r.status === "pending");

  return (
    <Modal title={t("balance.title")} onClose={onClose} width={560}>
      {err && (
        <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginBottom: 14,
                      padding: "10px 14px", background: `${T_NEW.danger}14`,
                      border: `1px solid ${T_NEW.danger}33`, borderRadius: T_NEW.r1,
                      fontFamily: T_NEW.fontSans }}>
          {err}
        </div>
      )}

      {data === null ? (
        <div style={{ color: T_NEW.ink56, fontSize: T_NEW.bodySm, padding: 28, textAlign: "center",
                      fontFamily: T_NEW.fontSans }}>
          {t("balance.loading")}
        </div>
      ) : (
        <>
          {/* ───── HERO: balance + redeem CTA ───────────────────────────
              This is the visual anchor for the whole modal. Big Serif
              persimmon dollar amount, with a dashed "passbook" border
              evoking a printed cashback book — fits the editorial
              concierge aesthetic. */}
          <div style={{ background: T_NEW.paperSoft, padding: "28px 20px", borderRadius: T_NEW.r3,
                        textAlign: "center", marginBottom: T_NEW.s6,
                        border: `1px dashed ${T_NEW.ink24 || T_NEW.ink12}`,
                        position: "relative" }}>
            <SectionLabel style={{ marginBottom: 12 }}>{t("balance.available")}</SectionLabel>
            <Serif size={T_NEW.hero || 38} color={T_NEW.persimmon} style={{ display: "block", lineHeight: 1, fontWeight: 600 }}>
              {fmt(data.balanceCents)}
            </Serif>
            <div style={{ marginTop: 18 }}>
              <Btn disabled={data.balanceCents < 500} onClick={() => setShowRedeem(s => !s)}>
                {showRedeem ? t("balance.cancel") : data.balanceCents < 500 ? t("balance.minRedeem") : t("balance.redeem")}
              </Btn>
            </div>
          </div>

          {/* Redemption form — only visible while user is opting in.
              Uses paper (not paperSoft) so it pops against the hero. */}
          {showRedeem && (
            <div style={{ background: T_NEW.paper, padding: 18, borderRadius: T_NEW.r2,
                          marginBottom: T_NEW.s6, border: `1px solid ${T_NEW.persimmon}40` }}>
              <SectionLabel>{t("balance.requestRedemption")}</SectionLabel>
              <Inp label={t("balance.amount")} value={redeemAmount} onChange={setRedeemAmount}
                   placeholder="5.00" autoFocus />
              {/* Cashback is platform-only: the single redemption method
                  is "credit toward the next order on BuyerGram." We used
                  to offer Amazon / Walmart gift cards but those were
                  external withdrawals and against the platform-only
                  policy, so they're gone. The hint below makes the
                  constraint explicit so the user isn't looking for a
                  withdrawal option that doesn't exist. */}
              <div style={{ marginBottom: 14,
                            padding: "10px 12px",
                            background: T_NEW.paperSoft,
                            border: `1px solid ${T_NEW.ink12}`,
                            borderRadius: T_NEW.r1,
                            fontSize: T_NEW.meta,
                            color: T_NEW.ink56,
                            fontFamily: T_NEW.fontSerif,
                            fontStyle: "italic",
                            lineHeight: 1.5 }}>
                {t("balance.method")}: <strong style={{ color: T_NEW.ink, fontStyle: "normal", fontFamily: T_NEW.fontSans }}>
                  {t("balance.method.orderDiscount")}
                </strong>
                <div style={{ marginTop: 6 }}>{t("balance.usageHint")}</div>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <Btn variant="ghost" onClick={() => setShowRedeem(false)}>{t("balance.cancel")}</Btn>
                <Btn disabled={submitting || !redeemAmount.trim()} onClick={submitRedemption}>
                  {submitting ? t("balance.submitting") : t("balance.submit")}
                </Btn>
              </div>
            </div>
          )}

          {/* ───── REFERRAL CARD ─────────────────────────────────────── */}
          {user?.referralCode && (
            <div style={{ background: T_NEW.paperSoft, padding: 16, borderRadius: T_NEW.r2,
                          marginBottom: T_NEW.s6, border: `1px solid ${T_NEW.ink12}` }}>
              <SectionLabel>{t("balance.referral.title")}</SectionLabel>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input readOnly value={refLink}
                       style={{ flex: 1, background: T_NEW.paper, color: T_NEW.ink,
                                border: `1px solid ${T_NEW.ink12}`, padding: "10px 12px",
                                borderRadius: T_NEW.r1, fontSize: T_NEW.meta,
                                fontFamily: T_NEW.fontMono }} />
                <Btn variant={copied ? "ghost" : "accent"} onClick={copyRefLink}>
                  {copied ? t("balance.copied") : t("balance.copy")}
                </Btn>
              </div>
              <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 10,
                            fontStyle: "italic", fontFamily: T_NEW.fontSerif, lineHeight: 1.5 }}>
                {t("balance.referral.body")}
              </div>
            </div>
          )}

          {/* ───── TELEGRAM LINK CARD ────────────────────────────────── */}
          <div style={{ background: T_NEW.paperSoft, padding: 16, borderRadius: T_NEW.r2,
                        marginBottom: T_NEW.s6, border: `1px solid ${T_NEW.ink12}` }}>
            <SectionLabel>{t("balance.telegram.title")}</SectionLabel>
            {tgLink ? (
              <>
                <div style={{ fontSize: T_NEW.bodySm, marginBottom: 8, color: T_NEW.ink,
                              fontFamily: T_NEW.fontSans, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: T_NEW.bottle, fontWeight: 700 }}>✓</span>
                  <span>
                    {t("balance.telegram.linked")}{" "}
                    <span style={{ fontWeight: 600 }}>
                      {tgLink.username ? `@${tgLink.username}` : tgLink.firstName || tgLink.telegramId}
                    </span>
                  </span>
                </div>
                <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginBottom: 10,
                              fontFamily: T_NEW.fontSans, lineHeight: 1.5 }}>
                  {t("balance.telegram.note")}
                </div>
                <Btn variant="ghost" onClick={async () => {
                  if (!await toast.confirm(t("balance.telegram.unlinkConfirm"))) return;
                  try { await api.unlinkTelegram(); setTgLink(null); } catch (e) { setTgErr(e.message); }
                }}>{t("balance.telegram.unlink")}</Btn>
              </>
            ) : tgCode ? (
              <>
                <div style={{ fontSize: T_NEW.meta, marginBottom: 10, color: T_NEW.ink,
                              fontFamily: T_NEW.fontSans, lineHeight: 1.55 }}>
                  {t("balance.telegram.openBot")}{" "}
                  <a href="https://t.me/Buyergrambot" target="_blank" rel="noopener noreferrer"
                     style={{ fontWeight: 700, color: T_NEW.persimmon, textDecoration: "underline" }}>
                    @Buyergrambot
                  </a>{" "}
                  {t("balance.telegram.thenSend")}
                </div>
                {/* The link command rendered like a receipt stamp */}
                <div style={{ background: T_NEW.paper, padding: "12px 16px", borderRadius: T_NEW.r1,
                              fontFamily: T_NEW.fontMono, fontSize: 18, letterSpacing: 1.5,
                              fontWeight: 700, color: T_NEW.persimmon, textAlign: "center",
                              marginBottom: 10, border: `1px dashed ${T_NEW.persimmon}66` }}>
                  /link {tgCode.code}
                </div>
                <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, fontFamily: T_NEW.fontSans,
                              textAlign: "center" }}>
                  {t("balance.telegram.codeExpires", { n: tgCode.expiresInMinutes })}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginBottom: 10,
                              fontFamily: T_NEW.fontSans, lineHeight: 1.55 }}>
                  {t("balance.telegram.unsetHelp")}
                </div>
                <Btn onClick={async () => {
                  setTgErr("");
                  try { setTgCode(await api.createTelegramLinkCode()); }
                  catch (e) { setTgErr(e.message); }
                }}>{t("balance.telegram.getCode")}</Btn>
              </>
            )}
            {tgErr && (
              <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginTop: 10,
                            fontFamily: T_NEW.fontSans }}>
                {tgErr}
              </div>
            )}
          </div>

          {/* ───── PENDING REDEMPTIONS ───────────────────────────────── */}
          {pendingReds.length > 0 && (
            <div style={{ marginBottom: T_NEW.s6 }}>
              <SectionLabel>{t("balance.pendingRedemptions")}</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pendingReds.map(r => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between",
                                            alignItems: "center", padding: "10px 14px",
                                            background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                                            border: `1px solid ${T_NEW.ink12}` }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: T_NEW.bodySm, color: T_NEW.ink,
                                    fontFamily: T_NEW.fontSans, textTransform: "capitalize" }}>
                        {r.method.replaceAll("_", " ")}
                      </div>
                      <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 2,
                                    fontFamily: T_NEW.fontMono }}>
                        {t("balance.requestedOn")} {new Date(r.requestedAt * 1000).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ color: T_NEW.persimmon, fontWeight: 700, fontFamily: T_NEW.fontMono,
                                  fontSize: T_NEW.bodySm }}>
                      −{fmt(r.amountCents)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ───── RECENT LEDGER (every credit/debit) ────────────────── */}
          <div style={{ marginBottom: T_NEW.s6 }}>
            <SectionLabel>{t("balance.recentLedger")}</SectionLabel>
            {data.recent.length === 0 ? (
              <div style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, padding: 18, textAlign: "center",
                            background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                            fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                {t("balance.ledger.empty")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.recent.map(e => (
                  <div key={e.id} style={{ display: "flex", justifyContent: "space-between",
                                            alignItems: "flex-start", padding: "10px 14px",
                                            background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                                            border: `1px solid ${T_NEW.ink12}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: T_NEW.bodySm, color: T_NEW.ink,
                                    fontFamily: T_NEW.fontSans, textTransform: "capitalize" }}>
                        {e.reason.replaceAll("_", " ")}
                      </div>
                      {e.note && (
                        <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 2,
                                      fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                          {e.note}
                        </div>
                      )}
                    </div>
                    <div style={{ color: e.deltaCents >= 0 ? T_NEW.bottle : T_NEW.danger,
                                  fontWeight: 700, fontFamily: T_NEW.fontMono, fontSize: T_NEW.bodySm,
                                  flexShrink: 0, marginLeft: 12 }}>
                      {e.deltaCents >= 0 ? "+" : ""}{fmt(e.deltaCents)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ───── RECENT EXTERNAL-MERCHANT TRANSACTIONS ─────────────── */}
          <div>
            <SectionLabel>{t("balance.recentTransactions")}</SectionLabel>
            {txs.length === 0 ? (
              <div style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, padding: 18, textAlign: "center",
                            background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                            fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                {t("balance.transactions.empty")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {/* Renamed map var from `t` to `tx` so we don't shadow the useT() `t` helper */}
                {txs.map(tx => (
                  <div key={tx.id} style={{ display: "flex", justifyContent: "space-between",
                                             alignItems: "center", padding: "10px 14px",
                                             background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                                             border: `1px solid ${T_NEW.ink12}` }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: T_NEW.bodySm, color: T_NEW.ink,
                                    fontFamily: T_NEW.fontSans }}>
                        {tx.merchant.name}
                      </div>
                      <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 2,
                                    fontFamily: T_NEW.fontMono }}>
                        {tx.status} · {t("balance.sale")} {fmt(tx.saleAmountCents)}
                      </div>
                    </div>
                    <div style={{ color: T_NEW.bottle, fontWeight: 700, fontFamily: T_NEW.fontMono,
                                  fontSize: T_NEW.bodySm }}>
                      +{fmt(tx.userCashbackCents)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
